/**
 * Sticky Mega Menu Enhancement - Updated for Grid Layout
 * 
 * This script adds enhanced functionality for the sticky featured content section:
 * - Detects when content is being scrolled
 * - Adds visual indicators
 * - Handles dynamic product switching per category
 * - Resets scroll position when switching categories
 */

class StickyMegaMenu {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.setupScrollDetection();
    this.setupProductSwitching();
  }

  /**
   * Add scroll detection to sticky content areas
   */
  setupScrollDetection() {
    const stickyContents = document.querySelectorAll('.mega-menu__content');
    
    stickyContents.forEach(content => {
      // Add scroll event listener
      content.addEventListener('scroll', () => {
        if (content.scrollTop > 10) {
          content.classList.add('is-scrolled');
        } else {
          content.classList.remove('is-scrolled');
        }
      });

      // Check initial scroll position
      if (content.scrollTop > 10) {
        content.classList.add('is-scrolled');
      }
    });
  }

  /**
   * Handle switching product displays based on hovered menu item
   */
  setupProductSwitching() {
    const menuLinks = document.querySelectorAll('.mega-menu__link[data-menu-target]');
    
    menuLinks.forEach(link => {
      link.addEventListener('mouseenter', (e) => {
        const targetIndex = e.currentTarget.getAttribute('data-menu-target');
        const grid = e.currentTarget.closest('.mega-menu__grid');
        
        if (grid) {
          // Remove active class from all links in this grid
          grid.querySelectorAll('.mega-menu__link').forEach(el => {
            el.classList.remove('active-hover');
          });
          
          // Add active class to current link
          e.currentTarget.classList.add('active-hover');
          
          // Show the correct product section
          this.showProductSection(targetIndex, grid);
        }
      });
    });
  }

  /**
   * Show the correct product section for the hovered menu item
   * @param {string} targetIndex - The index of the product section to show
   * @param {HTMLElement} grid - The mega menu grid container
   */
  showProductSection(targetIndex, grid) {
    // Hide all product sections within this grid
    const allProductSections = grid.querySelectorAll('.mega-menu__content[data-menu-products]');
    allProductSections.forEach(section => {
      section.style.display = 'none';
    });

    // Show the target product section
    const targetSection = grid.querySelector(`.mega-menu__content[data-menu-products="${targetIndex}"]`);
    if (targetSection) {
      targetSection.style.display = 'block';
      
      // Reset scroll position when switching to prevent showing middle of list
      this.resetScrollPosition(targetSection);
      
      // Remove scroll indicator class since we're at top
      targetSection.classList.remove('is-scrolled');
    }
  }

  /**
   * Reset scroll position to top when switching categories
   * @param {HTMLElement} element - The element to scroll to top
   */
  resetScrollPosition(element) {
    if (element) {
      element.scrollTo({
        top: 0,
        behavior: 'instant' // Use 'instant' for immediate jump, 'smooth' for animated
      });
    }
  }
}

/**
 * Intersection Observer for performance optimization
 * Only actively monitor menus that are visible in viewport
 */
class StickyMenuObserver {
  constructor() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('mega-menu--visible');
            // Could trigger lazy loading of images here
          } else {
            entry.target.classList.remove('mega-menu--visible');
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '50px' // Start loading slightly before visible
      }
    );

    this.observe();
  }

  observe() {
    const menus = document.querySelectorAll('.mega-menu__submenu-inner, .mega-menu__content');
    menus.forEach(menu => this.observer.observe(menu));
  }

  disconnect() {
    this.observer.disconnect();
  }
}

/**
 * Helper function to debounce scroll events for better performance
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initialize when script loads
let stickyMegaMenu;
let menuObserver;

// Use proper initialization to avoid conflicts
function initStickyMegaMenu() {
  if (!stickyMegaMenu) {
    stickyMegaMenu = new StickyMegaMenu();
  }
  
  if (!menuObserver) {
    menuObserver = new StickyMenuObserver();
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyMegaMenu);
} else {
  initStickyMegaMenu();
}

// Re-initialize on Shopify section reload (for theme editor)
document.addEventListener('shopify:section:load', function() {
  // Clean up old observers
  if (menuObserver) {
    menuObserver.disconnect();
    menuObserver = null;
  }
  
  // Re-initialize
  setTimeout(initStickyMegaMenu, 100);
});

/**
 * Export for use in theme if needed
 */
if (typeof window !== 'undefined') {
  window.StickyMegaMenu = StickyMegaMenu;
  window.StickyMenuObserver = StickyMenuObserver;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { StickyMegaMenu, StickyMenuObserver };
}